import PropTypes from "prop-types";
import React, { useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useItemStore } from "../../stores/items";
import { highlight } from "../../utils/highlight";

const SEARCH_LIMIT = 20;
const NO_ACTIVE_RESULT = -1;

function ItemSearchInput({ onAdd, existingNumbers, placeholder, inputRef }) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(NO_ACTIVE_RESULT);
  const [isOpen, setIsOpen] = useState(false);
  const resultsRef = useRef(null);
  const { fetchSome, byIds, allIds } = useItemStore(
    useShallow((state) => ({
      fetchSome: state.fetchSome,
      byIds: state.byIds,
      allIds: state.allIds,
    })),
  );

  const handleChange = (event) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    setActiveIndex(NO_ACTIVE_RESULT);
    setIsOpen(true);
    if (nextQuery.length >= 1) {
      fetchSome({ q: nextQuery, limit: SEARCH_LIMIT });
    }
  };

  const results = isOpen && query.length >= 1 ? allIds.map((id) => byIds[id]) : [];
  const filteredResults = results.filter((item) => !existingNumbers.includes(item.number));

  const addItem = (item) => {
    onAdd(item);
    setQuery("");
    setActiveIndex(NO_ACTIVE_RESULT);
  };

  // Only keyboard steps scroll the list. Hover follows real pointer moves (not mouseenter),
  // so results scrolling under a resting pointer keep the keyboard choice.
  const stepToResult = (index) => {
    setActiveIndex(index);
    resultsRef.current?.children[index]?.scrollIntoView({ block: "nearest" });
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      stepToResult(Math.min(activeIndex + 1, filteredResults.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      stepToResult(Math.max(activeIndex - 1, 0));
      return;
    }
    if (event.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(NO_ACTIVE_RESULT);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const activeItem = filteredResults[activeIndex];
      if (activeItem) {
        addItem(activeItem);
      }
    }
  };

  return (
    <div className="position-relative">
      <div className="input-group">
        <div className="input-group-text bg-light">
          <i className="fa fa-search opacity-5"></i>
        </div>
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          placeholder={`${placeholder}...`}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      </div>
      {query.length >= 1 && filteredResults.length > 0 && (
        <div
          ref={resultsRef}
          className="position-absolute w-100 bg-white border shadow-sm"
          style={{ zIndex: 10, maxHeight: "200px", overflowY: "auto" }}
        >
          {filteredResults.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className={`dropdown-item py-2 px-3${index === activeIndex ? " bg-gray-200" : ""}`}
              aria-current={index === activeIndex}
              onMouseMove={() => setActiveIndex(index)}
              onClick={() => addItem(item)}
            >
              <strong>{highlight(item.number, query)}</strong> - {highlight(item.name, query)}
              {item.groups?.[0] && <span className="text-muted ms-2">({highlight(item.groups[0].name, query)})</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

ItemSearchInput.propTypes = {
  onAdd: PropTypes.func.isRequired,
  existingNumbers: PropTypes.arrayOf(PropTypes.string).isRequired,
  placeholder: PropTypes.string.isRequired,
  inputRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

export default ItemSearchInput;
